import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import { type JourneyManager } from '../hooks/useJourney';
import { type useParentMessaging } from '../hooks/useParentMessaging';
import { driveEmbeddedFlow, EmbeddedFlowHTTPError } from './driveEmbeddedFlow';
import { buildSession } from './initSession';
import { getEnv } from '../utils/env';
import { type AppAction } from './appState';
import { type RonanInteractionContext } from '../types';

type ParentMessaging = ReturnType<typeof useParentMessaging>;

export function embeddedInit(
  dispatch: React.Dispatch<AppAction>,
  journey: JourneyManager,
  parentMessaging: ParentMessaging,
): (() => void) | void {
  if (parentMessaging.handshakeError) {
    dispatch({ action: 'INIT_ERROR', variant: 'techFail' });
    return;
  }
  if (!parentMessaging.initData) return;

  const initData = parentMessaging.initData;
  const abortController = new AbortController();
  const { signal } = abortController;

  const init = async () => {
    try {
      // window.location.origin is the gateway host in an iframe; the embedded
      // authorization endpoints live on the OAuth-SSI service.
      const baseUrl =
        getEnv('RONAN_OAUTH_SSI_URL').replace(/\/$/, '') ||
        window.location.origin;

      const result = await driveEmbeddedFlow(
        {
          baseUrl,
          clientId: initData.clientId,
          scope: initData.scope,
          state: initData.state,
          codeChallenge: initData.codeChallenge,
          webhookUri: initData.webhookUri,
          intentId: initData.intentId,
        },
        signal,
      );
      if (signal.aborted) return;

      // A non-embed-capable flow is hard-rejected at embedded/request.
      if (result.status === 'error') {
        dispatch({ action: 'INIT_ERROR', variant: 'deliveryModeMismatch' });
        return;
      }

      const interaction = YonduOAuthInteraction.fromURIs({
        authorization: result.authorization,
        interaction: result.interaction,
        startUri: result.startUri,
        finalizeUri: result.finalizeUri,
        resumeUri: result.resumeUri,
      });
      journey.setOAuthInteraction(interaction);

      const ctx = await interaction.start<RonanInteractionContext>();
      if (signal.aborted) return;

      // Verify the parent origin against the server-supplied allowlist.
      const allowedEmbedOrigins =
        ctx.deliveryModeSpecificConfig?.embedded?.allowedEmbedOrigins;
      if (!allowedEmbedOrigins) {
        dispatch({ action: 'INIT_ERROR', variant: 'deliveryModeMismatch' });
        return;
      }
      if (!parentMessaging.verifyOrigin(allowedEmbedOrigins)) {
        dispatch({ action: 'INIT_ERROR', variant: 'parentOriginNotAllowed' });
        return;
      }

      const sessionResult = await buildSession(ctx, { signal });
      if (sessionResult.status === 'aborted') return;
      if (sessionResult.status === 'error') {
        dispatch({ action: 'INIT_ERROR', variant: sessionResult.variant });
        return;
      }

      const { session } = sessionResult;
      dispatch({
        action: 'INIT_SUCCESS',
        session,
        autoSelectedToken:
          session.tokens.length === 1 ? session.tokens[0] : null,
        isMobile: false,
      });

      parentMessaging.notify({ type: 'ronan:ready' });
    } catch (e) {
      if (signal.aborted) return;

      if (e instanceof EmbeddedFlowHTTPError) {
        dispatch({
          action: 'INIT_ERROR',
          variant: e.status === 503 ? 'backendDowntime' : 'techFail',
        });
        return;
      }

      const isNetwork =
        e instanceof TypeError && e.message === 'Failed to fetch';
      dispatch({
        action: 'INIT_ERROR',
        variant: isNetwork ? 'networkLoss' : 'techFail',
      });
    }
  };

  init();

  return () => {
    abortController.abort();
  };
}
